import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Home, Phone } from "lucide-react";
import { T, tr } from "../lib/i18n";
import { tk, setCheckoutLang } from "../lib/checkoutI18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;

export default function ShopSuccessPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const cashOrderId = params.get("order_id");
  const [state, setState] = useState({ loading: true, order: null });
  const [lang] = useState(() => localStorage.getItem("lang") || "it");
  setCheckoutLang(lang);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    async function loadCash() {
      try {
        const { data } = await axios.get(`${API}/shop/orders/${cashOrderId}`);
        setState({ loading: false, order: data });
      } catch { setState({ loading: false, order: null }); }
    }

    async function pollStripe() {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/shop/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const ord = await axios.get(`${API}/shop/orders/${data.order_id}`);
          setState({ loading: false, order: ord.data });
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState({ loading: false, order: null, failed: true }); return;
        }
      } catch { /* transient */ }
      attempts++;
      if (attempts > 30) { setState({ loading: false, order: null, timeout: true }); return; }
      setTimeout(pollStripe, 2000);
    }
    if (sessionId) pollStripe();
    else if (cashOrderId) loadCash();
    else setState({ loading: false, order: null });
    return () => { cancelled = true; };
  }, [sessionId, cashOrderId]);

  if (state.loading) return <div className="min-h-screen bg-[#faf3ee] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#4a1c17]" /></div>;
  if (!state.order) return (
    <div className="min-h-screen bg-[#faf3ee] flex flex-col items-center justify-center gap-3 px-6">
      <XCircle className="h-14 w-14 text-red-500" />
      <h1 className="font-serif text-2xl font-bold text-[#2b1410]">Ordine non trovato</h1>
      <button onClick={() => nav("/shop")} className="rounded-full bg-[#4a1c17] text-[#f7ebdf] px-4 py-2 text-sm">{tr(T.backToShop, lang)}</button>
    </div>
  );

  const o = state.order;
  return (
    <div className="min-h-screen bg-[#faf3ee] py-10 px-4">
      <div className="max-w-lg mx-auto rounded-2xl bg-white border border-[#e8dcc8] p-6 shadow-sm">
        <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-600" />
        <h1 className="mt-3 text-center font-serif text-2xl font-bold text-[#2b1410]">{tr(T.thanks, lang)} {o.name}!</h1>
        <p className="mt-1 text-center text-sm text-[#7c5a4e]">{tr(T.yourOrderConfirmed, lang)} <strong>#{o.id.slice(0, 8)}</strong> {tr(T.isConfirmed, lang)}</p>

        <div className="mt-5 rounded-xl bg-[#faf3ee] border border-[#e8dcc8] p-4 space-y-1 text-sm">
          <Row label={tk("mode")} value={o.mode === "pickup" ? tk("pickupOnSite") : o.mode === "shipping" ? tk("shipping") : tk("ourDelivery")} />
          {o.address && <Row label={tk("address")} value={o.address} />}
          <Row label={tk("payment")} value={o.payment_status === "paid" ? tk("paidOnline") : `${tk("atPickup")} / ${tk("delivery").toLowerCase()}`} />
        </div>

        <div className="mt-4 rounded-xl bg-white border border-[#e8dcc8] p-4">
          <h3 className="font-serif font-bold text-[#2b1410] mb-2">Riepilogo</h3>
          <ul className="text-sm space-y-1">
            {o.items.map((it, i) => (
              <li key={i} className="flex justify-between"><span>{it.qty}× {it.name}</span><span className="text-[#7c5a4e]">{eur(it.line_total)}</span></li>
            ))}
          </ul>
          <div className="mt-2 pt-2 border-t border-[#e8dcc8] text-sm space-y-0.5">
            <Row label={tk("subtotal")} value={eur(o.subtotal)} />
            {o.shipping_fee > 0 && <Row label={tk("shipping")} value={eur(o.shipping_fee)} />}
            <Row label={tk("total")} value={eur(o.total)} bold />
          </div>
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          <a href="tel:+393518120603" className="flex-1 rounded-full border border-[#e8dcc8] text-[#4a1c17] px-4 py-2 text-sm text-center inline-flex items-center justify-center gap-1"><Phone className="h-3.5 w-3.5" /> {tk("call")}</a>
          <button onClick={() => nav("/shop")} className="flex-1 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-2 text-sm inline-flex items-center justify-center gap-1"><Home className="h-3.5 w-3.5" /> {tk("continueBtn")}</button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return <div className={`flex justify-between ${bold ? "font-bold text-[#2b1410] pt-1 border-t border-[#e8dcc8]" : "text-[#7c5a4e]"}`}><span>{label}</span><span className="text-right">{value}</span></div>;
}
